import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, FileText, Printer, Wallet, Trash2, History, Pencil } from "lucide-react";
import api, { API, TOKEN_KEY, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rp, num, pcs, pct, fmtDate } from "@/lib/format";
import { todayWib } from "@/components/PeriodPicker";
import { Panel, Stat, Pill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import TransactionModal from "@/components/TransactionModal";
import TrxActions from "@/components/TrxActions";

export default function WorkerDetail() {
  const { workerId } = useParams();
  const nav = useNavigate();
  const { isEditor, isOwner } = useAuth();
  const [data, setData] = useState(null);
  const [totalans, setTotalans] = useState([]);
  const [trxOpen, setTrxOpen] = useState(false);
  const [tab, setTab] = useState("jurnal");

  const load = () => api.get(`/workers/${workerId}/subledger`).then((r) => setData(r.data));
  const loadTotalans = () => api.get(`/totalan?worker_id=${workerId}`).then((r) => setTotalans(r.data)).catch(() => {});
  const reloadAll = () => { load(); loadTotalans(); };
  useEffect(() => { load(); loadTotalans(); /* eslint-disable-next-line */ }, [workerId]);

  if (!data) return <div className="text-slate-500">Memuat subledger…</div>;
  const w = data.worker;
  const m = data.metrics;
  const hist = data.history || [];
  const byType = (types) => hist.filter((t) => types.includes(t.transaction_type));
  const setoran = byType(["SETOR"]);
  const ambilRijek = byType(["AMBIL", "RIJEK"]);
  const kasbon = byType(["KASBON"]);

  return (
    <div>
      <button onClick={() => nav("/workers")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="w-4 h-4" /> Kembali</button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900">{w.worker_id}</h1>
            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${w.status === "ACTIVE" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-slate-100 text-slate-600 border-slate-300"}`}>{w.status}</span>
          </div>
          <p className="text-sm text-slate-500 mt-0.5">{w.worker_name} · {w.worker_type === "PENGEPUL" ? "Pengepul" : "Penjahit Dalam"}</p>
          <p className="text-xs text-slate-400 mt-0.5" data-testid="worker-detail-rekening">Rek. {w.no_rekening ? `${w.no_rekening}${w.nama_bank ? " · " + w.nama_bank : ""}` : "belum diisi"}{w.atas_nama ? ` · a.n. ${w.atas_nama}` : ""}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => nav(`/totalan?worker=${w.worker_id}`)} data-testid="worker-totalan-btn"><FileText className="w-4 h-4 mr-1.5" />Buat Totalan</Button>
          {isEditor && <Button onClick={() => setTrxOpen(true)} className="bg-slate-900 text-white" data-testid="worker-add-trx"><Plus className="w-4 h-4 mr-1.5" />Input Transaksi</Button>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Panel title="Ringkasan Produksi">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Stat label="Total Ambil" value={pcs(m.total_ambil)} tone="blue" />
            <Stat label="Total Setor" value={pcs(m.total_setor)} tone="emerald" />
            <Stat label="Total Good" value={pcs(m.total_good)} tone="emerald" />
            <Stat label="Total Reject" value={pcs(m.total_reject)} sub={pct(m.reject_rate)} tone="rose" />
            <Stat label="Open WIP" value={pcs(m.open_wip)} tone="amber" sub={m.wip_perlu_rekonsiliasi ? `PERLU REKONSILIASI · SETOR MELEBIHI AMBIL ${pcs(m.wip_selisih_rekonsiliasi)}` : undefined} />
          </div>
        </Panel>
        <Panel title="Ringkasan Keuangan">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Stat label="Gross Ongkos" value={rp(m.gross_sewing_pay)} />
            <Stat label="Bon" value={rp(m.total_bon)} tone="indigo" />
            <Stat label="Potongan Reject" value={rp(m.reject_deduction)} tone="rose" />
            <Stat label="Net Payable" value={rp(m.net_payable)} />
            <Stat label="Payment" value={rp(m.payment)} />
            <Stat label="Outstanding" value={rp(m.outstanding_payable)} tone="rose" />
          </div>
        </Panel>
      </div>

      {data.contoh?.length > 0 && <div className="mb-4 bg-amber-50 border-2 border-amber-400 rounded-lg p-3" data-testid="worker-contoh-reminder">
        <div className="text-sm font-extrabold text-amber-900">CONTOH BELUM DIKEMBALIKAN: {data.contoh.length} catatan · {num(data.contoh.reduce((a, c) => a + c.outstanding_qty, 0))} PCS</div>
        <div className="text-xs text-amber-800">{data.contoh.map((c) => <div key={c.sample_id}><span className="mono font-semibold">{c.sample_id}</span> · <span className="data-raw">{c.description}</span> · {num(c.outstanding_qty)} pcs · dibawa {fmtDate(c.taken_date)}{c.terlambat && <span className="text-rose-700 font-bold"> · TERLAMBAT</span>}</div>)}</div>
      </div>}

      <Panel className="overflow-visible">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="inline-flex flex-wrap h-auto w-auto gap-1 bg-[#F2F2F7] rounded-full p-1 mb-5" data-testid="worker-tabs">
            <TabTrigger value="jurnal" label="Jurnal" count={hist.length} />
            <TabTrigger value="setoran" label="Setoran" count={setoran.length} />
            <TabTrigger value="pengambilan" label="Pengambilan & Rijek" count={ambilRijek.length} />
            <TabTrigger value="kasbon" label="Kasbon Tunai" count={kasbon.length} />
            <TabTrigger value="totalan" label="Totalan" count={totalans.length} />
          </TabsList>

          <TabsContent value="jurnal"><TrxTable rows={hist} onDone={load} testId="tab-jurnal" empty="Belum ada transaksi." /></TabsContent>
          <TabsContent value="setoran"><TrxTable rows={setoran} onDone={load} testId="tab-setoran" empty="Belum ada setoran." /></TabsContent>
          <TabsContent value="pengambilan">
            <TrxTable rows={ambilRijek} onDone={load} testId="tab-pengambilan" empty="Belum ada pengambilan / rijek." />
            {(data.loans?.length > 0 || data.rjp?.length > 0) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-5">
                <div><div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Pinjaman Mendesak (PIN)</div>
                  <table className="w-full text-xs" data-testid="worker-loans"><tbody>{(data.loans || []).map((l) => <tr key={l.loan_id} className="border-b border-slate-100" data-testid={`worker-loan-${l.loan_id}`}><td className="py-1 mono font-semibold">{l.loan_id}</td><td className="py-1">{fmtDate(l.loan_date)}</td><td className="py-1 mono">{rp(l.amount)}</td><td className="py-1"><span className={`px-1.5 rounded text-[10px] font-bold ${l.status === "LUNAS" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{l.status}</span></td></tr>)}
                    {!data.loans?.length && <tr><td className="py-2 text-slate-400">Tidak ada</td></tr>}</tbody></table></div>
                <div><div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Reject ke Pengepul (RJP)</div>
                  <table className="w-full text-xs" data-testid="worker-rjp"><tbody>{(data.rjp || []).map((r) => <tr key={r.rjp_id} className="border-b border-slate-100" data-testid={`worker-rjp-${r.rjp_id}`}><td className="py-1 mono font-semibold">{r.rjp_id}</td><td className="py-1">{r.product_name} · {r.qty}</td><td className="py-1 text-slate-500">{r.pengepul_id === w.worker_id ? `asal ${r.source_worker_id}` : `pengepul ${r.pengepul_id}`}</td><td className="py-1"><span className="px-1.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700">{r.status}</span></td></tr>)}
                    {!data.rjp?.length && <tr><td className="py-2 text-slate-400">Tidak ada</td></tr>}</tbody></table></div>
              </div>
            )}
          </TabsContent>
          <TabsContent value="kasbon"><TrxTable rows={kasbon} onDone={load} testId="tab-kasbon" empty="Belum ada kasbon tunai." /></TabsContent>
          <TabsContent value="totalan"><TotalanTab totalans={totalans} workerId={w.worker_id} isOwner={isOwner} onChanged={reloadAll} nav={nav} /></TabsContent>
        </Tabs>
      </Panel>

      <TransactionModal open={trxOpen} onOpenChange={setTrxOpen} defaultWorker={w.worker_id} onDone={reloadAll} />
    </div>
  );
}

const TabTrigger = ({ value, label, count }) => (
  <TabsTrigger value={value} data-testid={`worker-tab-${value}`} className="rounded-full px-4 py-1.5 text-[13px] font-medium text-[#6E6E73] transition-colors hover:text-[#1D1D1F] data-[state=active]:bg-white data-[state=active]:text-[#0071E3] data-[state=active]:shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
    {label}{count > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#0071E3]/10 text-[#0071E3] text-[10px] font-bold">{count}</span>}
  </TabsTrigger>
);

function TrxTable({ rows, onDone, empty, testId }) {
  return (
    <div className="overflow-x-auto -mx-1" data-testid={testId}>
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600">
            {["Tanggal", "TRX ID", "Tipe", "Produk/Barang", "Qty", "Good", "Reject", "Jumlah", "Status", ""].map((h) => <th key={h} className="px-3 py-2.5">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.transaction_id} className="border-b border-slate-100 hover:bg-slate-50/80">
              <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{fmtDate(t.transaction_date)}</td>
              <td className="px-3 py-2.5 mono text-xs text-slate-400">{t.transaction_id}</td>
              <td className="px-3 py-2.5"><Pill kind="type" value={t.transaction_type} /></td>
              <td className="px-3 py-2.5 text-slate-700">{t.product_name || t.bon_item_name || "-"}{t.ref_no && <span className="ml-1.5 text-[10px] mono text-slate-400">Nomor Potongan {t.ref_no}</span>}</td>
              <td className="px-3 py-2.5 mono text-right">{t.quantity ? num(t.quantity) : "-"}</td>
              <td className="px-3 py-2.5 mono text-right text-emerald-700">{t.transaction_type === "SETOR" ? num(t.good_quantity) : "-"}</td>
              <td className="px-3 py-2.5 mono text-right text-rose-600">{t.reject_quantity ? num(t.reject_quantity) : "-"}</td>
              <td className="px-3 py-2.5 mono text-right font-semibold">{t.amount ? rp(t.amount) : "-"}</td>
              <td className="px-3 py-2.5"><Pill value={t.status} /></td>
              <td className="px-3 py-2.5"><TrxActions t={t} onDone={onDone} /></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={10} className="px-3 py-10 text-center text-slate-400">{empty}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function TotalanTab({ totalans, workerId, isOwner, onChanged, nav }) {
  const [payFor, setPayFor] = useState(null);
  const [histFor, setHistFor] = useState(null);
  const [delFor, setDelFor] = useState(null);

  const pdf = (id) => {
    fetch(`${API}/totalan/${id}/pdf`, { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); }).then((b) => {
        const url = URL.createObjectURL(b); const a = document.createElement("a"); a.href = url; a.download = `totalan_${workerId}_${id.slice(0, 8)}.pdf`; a.click(); URL.revokeObjectURL(url);
      }).catch(() => toast.error("Cetak PDF gagal"));
  };

  return (
    <div className="overflow-x-auto -mx-1" data-testid="worker-totalan-history">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600">
            <th className="px-3 py-2.5">Nomor</th>
            <th className="px-3 py-2.5">Tanggal</th>
            <th className="px-3 py-2.5 text-right">Hak Vendor</th>
            <th className="px-3 py-2.5 text-right">Dibayar</th>
            <th className="px-3 py-2.5 text-right">Sisa</th>
            <th className="px-3 py-2.5">Status</th>
            <th className="px-3 py-2.5">Aksi</th>
          </tr>
        </thead>
        <tbody>
          {totalans.map((t) => {
            const net = t.summary?.net || 0;
            const paid = t.paid_amount || 0;
            const out = net - paid;
            const st = t.payment_status || "BELUM_DIBAYAR";
            const lunas = st === "LUNAS";
            const nPay = (t.payments || []).length;
            const badge = { BELUM_DIBAYAR: "bg-slate-100 text-slate-700 border-slate-300", DIBAYAR_SEBAGIAN: "bg-amber-100 text-amber-800 border-amber-300", LUNAS: "bg-emerald-100 text-emerald-800 border-emerald-300" }[st];
            return (
              <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50/80 align-middle" data-testid={`worker-totalan-row-${t.id}`}>
                <td className="px-3 py-3"><div className="mono text-xs font-semibold text-slate-700">{(t.totalan_id || t.id).slice(0, 8).toUpperCase()}</div><div className="text-[10px] text-slate-400">{t.period || "BULANAN"} {t.period_label || t.month}</div></td>
                <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{fmtDate(t.tanggal_totalan)}</td>
                <td className="px-3 py-3 mono text-right font-semibold">{rp(net)}</td>
                <td className="px-3 py-3 mono text-right text-blue-700">{rp(paid)}</td>
                <td className="px-3 py-3 mono text-right text-rose-700 font-semibold">{rp(out)}</td>
                <td className="px-3 py-3"><span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-bold border ${badge}`} data-testid={`worker-totalan-status-${t.id}`}>{lunas ? "Lunas" : st === "DIBAYAR_SEBAGIAN" ? "Dibayar Sebagian" : "Belum Dibayar"}</span></td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => pdf(t.id)} className="p-1.5 rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900" title="Cetak PDF" data-testid={`worker-totalan-pdf-${t.id}`}><Printer className="w-4 h-4" /></button>
                    {!lunas && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-300" data-testid={`worker-totalan-unpaid-${t.id}`}>Unpaid</span>}
                    {isOwner && !lunas && <Button size="sm" variant="outline" className="h-7 border-[#0071E3] text-[#0071E3] hover:bg-blue-50" onClick={() => setPayFor(t)} data-testid={`worker-totalan-pay-${t.id}`}><Wallet className="w-3.5 h-3.5 mr-1" />Catat Pembayaran</Button>}
                    <Button size="sm" variant="outline" className="h-7" onClick={() => setHistFor(t)} data-testid={`worker-totalan-riwayat-${t.id}`}><History className="w-3.5 h-3.5 mr-1" />Riwayat ({nPay})</Button>
                    {isOwner && <Button size="sm" variant="outline" className="h-7 border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-40" disabled={paid > 0} title={paid > 0 ? "Tidak bisa dihapus — sudah ada pembayaran" : "Hapus totalan"} onClick={() => setDelFor(t)} data-testid={`worker-totalan-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5 mr-1" />Hapus</Button>}
                  </div>
                </td>
              </tr>
            );
          })}
          {totalans.length === 0 && <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-400">Belum ada totalan untuk pekerja ini. Klik <b>Buat Totalan</b> di atas untuk membuat.</td></tr>}
        </tbody>
      </table>

      <PayDialog totalan={payFor} onClose={() => setPayFor(null)} onDone={onChanged} />
      <HistoryDialog totalan={histFor} isOwner={isOwner} onClose={() => setHistFor(null)} onDone={onChanged} />
      <DeleteDialog totalan={delFor} onClose={() => setDelFor(null)} onDone={onChanged} />
    </div>
  );
}

function PayDialog({ totalan, onClose, onDone }) {
  const [form, setForm] = useState({ amount: "", tanggal: todayWib(), metode: "TUNAI", catatan: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (totalan) setForm({ amount: "", tanggal: todayWib(), metode: "TUNAI", catatan: "" }); }, [totalan]);
  if (!totalan) return null;
  const net = totalan.summary?.net || 0;
  const paid = totalan.paid_amount || 0;
  const sisa = net - paid;
  const submit = async () => {
    const amt = parseInt(form.amount || 0);
    if (!amt || amt <= 0) return toast.error("Jumlah pembayaran harus lebih dari 0");
    setBusy(true);
    try {
      const { data } = await api.post(`/totalan/${totalan.id}/pay`, { amount: amt, tanggal: form.tanggal, metode: form.metode, catatan: form.catatan || null });
      toast.success(data.message || "Pembayaran tercatat");
      onClose(); onDone && onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!totalan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="worker-pay-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wallet className="w-4 h-4 text-emerald-600" />Catat Pembayaran</DialogTitle>
          <DialogDescription>Pembayaran dicatat ke Jurnal Umum sebagai transaksi <b>BAYAR</b>. Sisa tagihan: <b>{rp(sisa)}</b> (NET {rp(net)} − dibayar {rp(paid)}).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Jumlah (Rp)</label>
            <Input type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder={`maks. ${rp(sisa)}`} data-testid="worker-pay-amount" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Tanggal</label>
              <Input type="date" value={form.tanggal} onChange={(e) => setForm((f) => ({ ...f, tanggal: e.target.value }))} data-testid="worker-pay-date" /></div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Metode</label>
              <Select value={form.metode} onValueChange={(v) => setForm((f) => ({ ...f, metode: v }))}><SelectTrigger data-testid="worker-pay-method"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="TUNAI">Tunai</SelectItem><SelectItem value="TRANSFER">Transfer</SelectItem></SelectContent></Select></div>
          </div>
          <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Catatan (opsional)</label>
            <Input value={form.catatan} onChange={(e) => setForm((f) => ({ ...f, catatan: e.target.value }))} placeholder="mis. cicilan pertama" data-testid="worker-pay-note" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="worker-pay-cancel">Batal</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="worker-pay-confirm"><Wallet className="w-4 h-4 mr-1.5" />{busy ? "Menyimpan…" : "Catat Pembayaran"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ totalan, isOwner, onClose, onDone }) {
  const [t, setT] = useState(null);
  const [editId, setEditId] = useState(null);
  const [delId, setDelId] = useState(null);
  const [form, setForm] = useState({ amount: "", tanggal: "", metode: "TUNAI", catatan: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setT(totalan); setEditId(null); setDelId(null); }, [totalan]);
  if (!t) return null;
  const payments = t.payments || [];
  const net = t.summary?.net || 0;
  const paid = t.paid_amount || 0;
  const startEdit = (p) => { setDelId(null); setEditId(p.payment_id); setForm({ amount: String(p.amount), tanggal: p.tanggal, metode: p.metode || "TUNAI", catatan: p.catatan || "" }); };
  const saveEdit = async (pid) => {
    const amt = parseInt(form.amount || 0);
    if (!amt || amt <= 0) return toast.error("Jumlah harus lebih dari 0");
    setBusy(true);
    try {
      const { data } = await api.put(`/totalan/${t.id}/pay/${pid}`, { amount: amt, tanggal: form.tanggal, metode: form.metode, catatan: form.catatan || null });
      toast.success(data.message || "Pembayaran diperbarui"); setT(data.totalan); setEditId(null); onDone && onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const doDelete = async (pid) => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/totalan/${t.id}/pay/${pid}`);
      toast.success(data.message || "Pembayaran dihapus"); setT(data.totalan); setDelId(null); onDone && onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!totalan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="worker-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="w-4 h-4 text-slate-600" />Riwayat Pembayaran</DialogTitle>
          <DialogDescription>{(t.totalan_id || t.id).slice(0, 8).toUpperCase()} · NET {rp(net)} · Dibayar {rp(paid)} · Sisa {rp(net - paid)}</DialogDescription>
        </DialogHeader>
        {payments.length === 0 ? <div className="text-sm text-slate-400 py-4 text-center">Belum ada pembayaran.</div> : (
          <div className="space-y-1.5">
            {payments.map((p) => editId === p.payment_id ? (
              <div key={p.payment_id} className="rounded-lg border border-[#0071E3]/30 bg-blue-50/40 p-3 space-y-2" data-testid={`worker-payment-edit-${p.payment_id}`}>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-[10px] uppercase tracking-wider text-slate-500">Jumlah</label><Input type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className="h-8" data-testid="worker-payment-edit-amount" /></div>
                  <div><label className="text-[10px] uppercase tracking-wider text-slate-500">Tanggal</label><Input type="date" value={form.tanggal} onChange={(e) => setForm((f) => ({ ...f, tanggal: e.target.value }))} className="h-8" /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-[10px] uppercase tracking-wider text-slate-500">Metode</label><Select value={form.metode} onValueChange={(v) => setForm((f) => ({ ...f, metode: v }))}><SelectTrigger className="h-8"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="TUNAI">Tunai</SelectItem><SelectItem value="TRANSFER">Transfer</SelectItem></SelectContent></Select></div>
                  <div><label className="text-[10px] uppercase tracking-wider text-slate-500">Catatan</label><Input value={form.catatan} onChange={(e) => setForm((f) => ({ ...f, catatan: e.target.value }))} className="h-8" /></div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" className="h-7" onClick={() => setEditId(null)} data-testid={`worker-payment-edit-cancel-${p.payment_id}`}>Batal</Button>
                  <Button size="sm" className="h-7 bg-[#0071E3] text-white hover:bg-[#0077ED]" disabled={busy} onClick={() => saveEdit(p.payment_id)} data-testid={`worker-payment-save-${p.payment_id}`}>Simpan</Button>
                </div>
              </div>
            ) : (
              <div key={p.payment_id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 px-3 py-2 hover:bg-slate-50" data-testid={`worker-payment-row-${p.payment_id}`}>
                <div className="min-w-0"><div className="mono text-sm font-semibold">{rp(p.amount)}</div><div className="text-[11px] text-slate-500">{fmtDate(p.tanggal)} · {p.metode}{p.catatan ? ` · ${p.catatan}` : ""}</div></div>
                {delId === p.payment_id ? (
                  <div className="flex items-center gap-1.5 shrink-0"><span className="text-[11px] text-rose-700">Hapus?</span><Button size="sm" variant="outline" className="h-7 border-rose-300 text-rose-700 hover:bg-rose-50" disabled={busy} onClick={() => doDelete(p.payment_id)} data-testid={`worker-payment-delete-confirm-${p.payment_id}`}>Ya, hapus</Button><Button size="sm" variant="outline" className="h-7" onClick={() => setDelId(null)}>Batal</Button></div>
                ) : isOwner ? (
                  <div className="flex items-center gap-1 shrink-0"><button onClick={() => startEdit(p)} className="p-1.5 rounded-full text-slate-500 hover:bg-blue-50 hover:text-[#0071E3]" title="Edit" data-testid={`worker-payment-edit-btn-${p.payment_id}`}><Pencil className="w-3.5 h-3.5" /></button><button onClick={() => { setEditId(null); setDelId(p.payment_id); }} className="p-1.5 rounded-full text-slate-500 hover:bg-rose-50 hover:text-rose-600" title="Hapus" data-testid={`worker-payment-delete-btn-${p.payment_id}`}><Trash2 className="w-3.5 h-3.5" /></button></div>
                ) : null}
              </div>
            ))}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="worker-history-close">Tutup</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ totalan, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (totalan) setReason(""); }, [totalan]);
  if (!totalan) return null;
  const submit = async () => {
    if (!reason.trim()) return toast.error("Alasan wajib diisi");
    setBusy(true);
    try {
      const { data } = await api.delete(`/totalan/${totalan.id}?alasan=${encodeURIComponent(reason.trim())}`);
      toast.success(data.message || "Totalan dihapus");
      onClose(); onDone && onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!totalan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="worker-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Trash2 className="w-4 h-4 text-rose-600" />Hapus Totalan</DialogTitle>
          <DialogDescription>Totalan dihapus permanen & semua efek dibatalkan (pinjaman kembali <b>BELUM LUNAS</b>, sumber tidak lagi terkunci). <b>Data setoran/bon TIDAK dihapus.</b> Tercatat di audit log.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Alasan (wajib)</label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="mis. totalan dibuat ganda / salah periode" data-testid="worker-delete-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="worker-delete-cancel">Batal</Button>
          <Button onClick={submit} disabled={busy || !reason.trim()} className="bg-rose-600 text-white hover:bg-rose-700" data-testid="worker-delete-confirm"><Trash2 className="w-4 h-4 mr-1.5" />{busy ? "Menghapus…" : "Hapus Permanen"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
